import { FetchedReportData } from "../../../types";
import { formatDate } from "../../../utils/dateUtils";
import { formattedVehicleInfo } from "../../SubmitTip/resources";
import AudioPlayer from "../../common/AudioPlayer";
import ImageGallery from "../../common/ImageGallery";
import TruncatedText from "../../common/TruncatedText";
import {
  capitalizeFirstLetters,
  getMediaUrl,
} from "../../../utils/stringUtils";
import { Badge } from "./Badge";
import {
  FaClock,
  FaMapMarkerAlt,
  FaUser,
  FaUserSecret,
  FaCar,
  FaExclamationTriangle,
} from "react-icons/fa";
// import ReportStatusSelector from "../../Admin/AdminDashboard/ReportStatusSelector";

// Card section component for displaying information in a consistent way
const InfoSection = ({
  title,
  icon,
  children,
  className = "",
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`bg-white rounded-lg p-6 shadow-sm ${className}`}>
    <div className="flex items-center gap-2 mb-4 text-gray-7">
      {icon}
      <h3 className="font-bold text-lg">{title}</h3>
    </div>
    <div className="text-gray-7">{children}</div>
  </div>
);

// InfoItem component for consistent label/value display
const InfoItem = ({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string | React.ReactNode;
  className?: string;
}) => (
  <div className={`mb-4 ${className}`}>
    <p className="text-sm text-gray-6 mb-1">{label}</p>
    <div className="text-base font-medium">{value || "Not Available"}</div>
  </div>
);

const UserReportViewCard = ({
  report,
}: {
  report: FetchedReportData | undefined;
}) => {
  if (!report) {
    return null;
  }

  const {
    _id,
    reportId,
    caseName,
    caseType,
    incidentDescription,
    hasAbuserInfo,
    reporterDetails,
    abuserDetails,
    status,
    createdAt,
  } = report;

  const formattedDate = incidentDescription?.date
    ? formatDate(incidentDescription.date, "mmddyyyy")
    : "Not Available";

  const reportDate = formatDate(createdAt, "mmddyyyy");

  // Handle voice narration audio
  const voiceNarrationUrl = incidentDescription?.incidentVoiceNarration
    ? getMediaUrl(incidentDescription.incidentVoiceNarration[0])
    : "";

  // Handle incident evidence images
  const evidenceImageUrls = incidentDescription?.incidentPhotoEvidence
    ? incidentDescription.incidentPhotoEvidence.map(getMediaUrl)
    : [];

  // Handle abuser photo evidence
  const abuserPhotoUrls =
    hasAbuserInfo && abuserDetails?.abuserPhotoDescription
      ? abuserDetails.abuserPhotoDescription.map(getMediaUrl)
      : [];

  // Get status color
  const getStatusColor = () => {
    switch (status) {
      case "awaiting review":
        return "bg-amber-100 text-amber-800";
      case "under review":
        return "bg-blue-100 text-blue-800";
      case "reviewed":
        return "bg-green-100 text-green-800";
      case "submitted":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="w-full">
      {/* Header section with case ID and status */}
      <div className="bg-gray-7 text-white p-6 rounded-t-lg">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <p className="text-sm opacity-90">Report ID: {reportId}</p>
            <h2 className="text-2xl font-bold mt-1">
              Case #<TruncatedText text={_id} />
            </h2>
          </div>
          <div className="flex flex-col items-end">
            <Badge
              className={`${getStatusColor()} font-medium capitalize px-4 py-1.5 rounded-full`}
            >
              {status}
            </Badge>
            <p className="text-sm mt-2">Submitted: {reportDate}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {/* Basic case information */}
        <InfoSection
          title="Case Information"
          icon={<FaExclamationTriangle />}
          className="md:col-span-2"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem
              label="Report Type"
              value={capitalizeFirstLetters(caseName)}
            />
            <InfoItem
              label="Subcategory"
              value={capitalizeFirstLetters(caseType)}
            />
            <InfoItem label="Incident Date" value={formattedDate} />
            <InfoItem
              label="Incident Status"
              value={
                <Badge
                  className={`${getStatusColor()} font-medium capitalize px-3 py-1 rounded-full`}
                >
                  {status}
                </Badge>
                // <ReportStatusSelector reportStatus={status} simpleView disbaleDropdown />
              }
            />
          </div>
        </InfoSection>

        {/* Location information */}
        <InfoSection
          title="Location Details"
          icon={<FaMapMarkerAlt />}
          className="md:col-span-2"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem
              label="Location Type"
              value={capitalizeFirstLetters(
                incidentDescription?.location || ""
              )}
            />
            <InfoItem
              label="Detailed Location"
              value={`${capitalizeFirstLetters(
                incidentDescription?.detailedLocation.city || ""
              )}, 
              ${capitalizeFirstLetters(
                incidentDescription?.detailedLocation.county || ""
              )}, 
              ${capitalizeFirstLetters(
                incidentDescription?.detailedLocation.state || ""
              )}`}
            />

            {/* Digital links if applicable */}
            {["social media", "online"].includes(
              incidentDescription?.location || ""
            ) &&
              incidentDescription?.digitalcaseLinks &&
              incidentDescription.digitalcaseLinks.length > 0 && (
                <div className="md:col-span-2">
                  <InfoItem
                    label="Online URLs"
                    value={
                      <div className="flex flex-col gap-2">
                        {incidentDescription.digitalcaseLinks.map(
                          (link, index) => (
                            <a
                              key={index}
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-7 hover:underline truncate"
                            >
                              {link}
                            </a>
                          )
                        )}
                      </div>
                    }
                  />
                </div>
              )}
          </div>
        </InfoSection>

        {/* Incident description */}
        <InfoSection
          title="Incident Details"
          icon={<FaClock />}
          className="md:col-span-2"
        >
          <InfoItem
            label="Description"
            value={
              <p className="whitespace-pre-wrap">
                {incidentDescription?.description || "No description provided"}
              </p>
            }
          />

          {/* Evidence section */}
          <div className="mt-6">
            <h4 className="font-semibold mb-4 text-lg">Evidence</h4>

            {/* Voice narration */}
            {voiceNarrationUrl ? (
              <div className="mb-6">
                <p className="text-sm text-gray-6 mb-2">Voice Narration</p>
                <AudioPlayer
                  src={voiceNarrationUrl}
                  title="Incident Voice Narration"
                />
              </div>
            ) : (
              <InfoItem
                label="Voice Narration"
                value="No voice narration provided"
              />
            )}

            {/* Photo evidence */}
            {evidenceImageUrls.length > 0 ? (
              <div className="mb-6">
                <p className="text-sm text-gray-6 mb-2">Photo Evidence</p>
                <ImageGallery
                  images={evidenceImageUrls}
                  title="Incident Evidence Photos"
                />
              </div>
            ) : (
              <InfoItem
                label="Photo Evidence"
                value="No photo evidence provided"
              />
            )}
          </div>
        </InfoSection>

        {/* Reporter information */}
        <InfoSection title="My Information" icon={<FaUser />}>
          <InfoItem
            label="Role"
            value={capitalizeFirstLetters(reporterDetails?.role || "")}
          />
          <InfoItem
            label="Gender"
            value={capitalizeFirstLetters(reporterDetails?.gender || "")}
          />
          <InfoItem label="Age Range" value={reporterDetails?.age || ""} />

          {/* Only show contact info if there's any */}
          {(reporterDetails?.phone || reporterDetails?.email) && (
            <InfoItem
              label="Contact"
              value={
                <div>
                  {reporterDetails?.phone && <p>{reporterDetails.phone}</p>}
                  {reporterDetails?.email && <p>{reporterDetails.email}</p>}
                </div>
              }
            />
          )}

          {/* Social media handles */}
          {Object.entries(reporterDetails?.socials || {}).some(
            ([, handle]) => handle
          ) && (
            <InfoItem
              label="Social Media"
              value={
                <div>
                  {Object.entries(reporterDetails?.socials || {})
                    .filter(([, handle]) => handle)
                    .map(([platform, handle]) => (
                      <p key={platform}>
                        {platform}: {handle}
                      </p>
                    ))}
                </div>
              }
            />
          )}
        </InfoSection>

        {/* Abuser information - only show if exists */}
        {hasAbuserInfo && (
          <InfoSection title="Abuser Information" icon={<FaUserSecret />}>
            <InfoItem label="Name" value={abuserDetails?.name || ""} />
            {abuserDetails?.nickname && (
              <InfoItem label="Nickname" value={abuserDetails.nickname} />
            )}
            <InfoItem
              label="Gender"
              value={capitalizeFirstLetters(abuserDetails?.gender || "")}
            />
            <InfoItem label="Age Range" value={abuserDetails?.refAge || ""} />
            <InfoItem
              label="Relation to Victim"
              value={abuserDetails?.relationToVictim || ""}
            />
            <InfoItem
              label="Occupation"
              value={abuserDetails?.occupation || ""}
            />

            {/* Special authority info if applicable */}
            {abuserDetails?.hasSpecialAuthority && (
              <InfoItem
                label="Authority Position"
                value={abuserDetails.specialAuthorityPosition || ""}
              />
            )}

            <InfoItem
              label="Physical Description"
              value={
                <p className="whitespace-pre-wrap">
                  {abuserDetails?.phyDescription || "No description provided"}
                </p>
              }
            />

            {/* Abuser photos */}
            {abuserPhotoUrls.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-6 mb-2">Abuser Photos</p>
                <ImageGallery
                  images={abuserPhotoUrls}
                  title="Photos of the Abuser"
                />
              </div>
            )}

            {/* Vehicle info if applicable */}
            {abuserDetails?.hasAbuserVehicleInfo &&
              abuserDetails.vehicleInfo && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCar />
                    <p className="font-semibold">Vehicle Information</p>
                  </div>
                  <p>{formattedVehicleInfo(abuserDetails.vehicleInfo)}</p>
                </div>
              )}

            {/* Social media handles */}
            {Object.entries(abuserDetails?.socials || {}).some(
              ([, handle]) => handle
            ) && (
              <InfoItem
                label="Social Media"
                value={
                  <div>
                    {Object.entries(abuserDetails?.socials || {})
                      .filter(([, handle]) => handle)
                      .map(([platform, handle]) => (
                        <p key={platform}>
                          {platform}: {handle}
                        </p>
                      ))}
                  </div>
                }
              />
            )}
          </InfoSection>
        )}
      </div>
    </div>
  );
};

export default UserReportViewCard;
